import React, { useEffect, useState } from "react";
import { api, STATUS_OPTIONS, TASK_TYPES } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusToggle from "../components/StatusToggle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, ExternalLink, Trash2, X, LayoutGrid, List as ListIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { celebrate } from "../lib/fx";
import { monthRange, isInWeek } from "../lib/weeks";
import TaskDetailModal from "../components/TaskDetailModal";

const EMPTY = {
  title: "",
  ref_video_link: "",
  assigned_editor_id: "",
  assigned_smm_id: "",
  client_id: "",
  status: "pending",
  task_type: "video_edit",
  due_date: "",
  reminder_at: "",
  notes: "",
  published_url: "",
  custom_fields: {},
};

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

export default function Schedule() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMonth, setFilterMonth] = useState(currentMonth());
  const [filterWeek, setFilterWeek] = useState("all");
  const [view, setView] = useState("kanban");
  const [dragOver, setDragOver] = useState(null);
  const [customRows, setCustomRows] = useState([]);
  const [detailTask, setDetailTask] = useState(null);

  const canEdit = ["admin", "editor", "smm"].includes(user?.role);
  const canDelete = ["admin", "smm"].includes(user?.role);

  const load = async () => {
    const [t, u, c] = await Promise.all([
      api.get("/tasks"),
      api.get("/users"),
      api.get("/clients"),
    ]);
    setTasks(t.data);
    setUsers(u.data);
    setClients(c.data);
  };

  useEffect(() => {
    load();
  }, []);

  const editors = users.filter((u) => u.role === "editor" || u.role === "admin");
  const smms = users.filter((u) => u.role === "smm" || u.role === "admin");

  const monthFiltered = tasks.filter((t) => {
    if (!filterMonth) return true;
    const d = t.due_date || t.created_at || "";
    return d.startsWith(filterMonth);
  });
  const weeks = filterMonth ? monthRange(filterMonth) : [];
  const weekObj = weeks.find((w) => w.key === filterWeek);
  const weekFiltered = !weekObj
    ? monthFiltered
    : monthFiltered.filter((t) => isInWeek(t.due_date, weekObj));
  const filtered =
    filterStatus === "all"
      ? weekFiltered
      : weekFiltered.filter((t) => t.status === filterStatus);

  const onDragStart = (e, task) => {
    e.dataTransfer.setData("text/plain", task.task_id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOverCol = (e, status) => {
    e.preventDefault();
    setDragOver(status);
  };
  const onDropCol = (e, status) => {
    e.preventDefault();
    setDragOver(null);
    const taskId = e.dataTransfer.getData("text/plain");
    const task = tasks.find((t) => t.task_id === taskId);
    if (task && task.status !== status) {
      handleStatusChange(task, status);
    }
  };

  const findUser = (id) => users.find((u) => u.user_id === id);
  const findClient = (id) => clients.find((c) => c.client_id === id);

  const handleStatusChange = async (task, status) => {
    try {
      const r = await api.patch(`/tasks/${task.task_id}`, { status });
      setTasks((prev) =>
        prev.map((t) => (t.task_id === task.task_id ? r.data : t))
      );
      toast.success(`Status updated`);
      if (status === "posted" && task.status !== "posted") {
        celebrate();
      }
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try {
      await api.delete(`/tasks/${task.task_id}`);
      setTasks((prev) => prev.filter((t) => t.task_id !== task.task_id));
      toast.success("Task deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const openCreate = () => {
    setForm(EMPTY);
    setCustomRows([]);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.title) {
      toast.error("Title required");
      return;
    }
    const custom_fields = {};
    customRows.forEach((r) => {
      if (r.key) custom_fields[r.key] = r.value;
    });
    const payload = {
      ...form,
      custom_fields,
      assigned_editor_id: form.assigned_editor_id || null,
      assigned_smm_id: form.assigned_smm_id || null,
      client_id: form.client_id || null,
      due_date: form.due_date || null,
      reminder_at: form.reminder_at ? new Date(form.reminder_at).toISOString() : null,
    };
    try {
      const r = await api.post("/tasks", payload);
      setTasks((prev) => [r.data, ...prev]);
      setOpen(false);
      toast.success("Task created");
    } catch (e) {
      toast.error("Failed to create task");
    }
  };

  const addCustomRow = () => setCustomRows((p) => [...p, { key: "", value: "" }]);
  const updateCustomRow = (i, k, v) =>
    setCustomRows((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const removeCustomRow = (i) =>
    setCustomRows((p) => p.filter((_, idx) => idx !== i));

  return (
    <div data-testid="schedule-page">
      <PageHeader
        eyebrow="Weekly Schedule"
        title="Editing Pipeline"
        description="Track every edit from brief to published. Toggle status inline."
        actions={
          canEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={openCreate}
                  data-testid="new-task-btn"
                  className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black"
                >
                  <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  <span className="uppercase tracking-[0.15em] text-xs">New Task</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-neutral-950 border border-neutral-800 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">New Task</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Title</Label>
                    <Input
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="mt-2 rounded-none bg-neutral-900 border-neutral-800"
                      data-testid="task-title-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Reference Video Link</Label>
                    <Input
                      value={form.ref_video_link}
                      placeholder="https://drive.google.com/..."
                      onChange={(e) => setForm({ ...form, ref_video_link: e.target.value })}
                      className="mt-2 rounded-none bg-neutral-900 border-neutral-800"
                      data-testid="task-ref-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Task Type</Label>
                    <Select
                      value={form.task_type}
                      onValueChange={(v) => setForm({ ...form, task_type: v })}
                    >
                      <SelectTrigger className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="task-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                        {TASK_TYPES.map((tt) => (
                          <SelectItem key={tt.value} value={tt.value}>{tt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Client</Label>
                    <Select
                      value={form.client_id || "none"}
                      onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="task-client-select">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                        <SelectItem value="none">—</SelectItem>
                        {clients.map((c) => (
                          <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Due Date</Label>
                    <Input
                      type="date"
                      value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                      className="mt-2 rounded-none bg-neutral-900 border-neutral-800"
                      data-testid="task-due-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Reminder (optional)</Label>
                    <Input
                      type="datetime-local"
                      value={form.reminder_at}
                      onChange={(e) => setForm({ ...form, reminder_at: e.target.value })}
                      className="mt-2 rounded-none bg-neutral-900 border-neutral-800"
                      data-testid="task-reminder-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Editor</Label>
                    <Select
                      value={form.assigned_editor_id || "none"}
                      onValueChange={(v) => setForm({ ...form, assigned_editor_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="task-editor-select">
                        <SelectValue placeholder="Assign editor" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                        <SelectItem value="none">—</SelectItem>
                        {editors.map((u) => (
                          <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">SMM</Label>
                    <Select
                      value={form.assigned_smm_id || "none"}
                      onValueChange={(v) => setForm({ ...form, assigned_smm_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="task-smm-select">
                        <SelectValue placeholder="Assign SMM" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                        <SelectItem value="none">—</SelectItem>
                        {smms.map((u) => (
                          <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Notes</Label>
                    <Textarea
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="mt-2 rounded-none bg-neutral-900 border-neutral-800 min-h-[80px]"
                      data-testid="task-notes-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Custom Fields</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={addCustomRow}
                        data-testid="add-custom-field"
                        className="h-7 rounded-none text-xs uppercase tracking-wider hover:bg-neutral-900"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Field
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {customRows.map((r, i) => (
                        <div key={i} className="grid grid-cols-[1fr,1fr,auto] gap-2">
                          <Input
                            placeholder="Field name"
                            value={r.key}
                            onChange={(e) => updateCustomRow(i, "key", e.target.value)}
                            className="rounded-none bg-neutral-900 border-neutral-800"
                          />
                          <Input
                            placeholder="Value"
                            value={r.value}
                            onChange={(e) => updateCustomRow(i, "value", e.target.value)}
                            className="rounded-none bg-neutral-900 border-neutral-800"
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => removeCustomRow(i)}
                            className="rounded-none hover:bg-neutral-900"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button
                    onClick={submit}
                    data-testid="task-submit-btn"
                    className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black"
                  >
                    Create Task
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <div className="px-6 lg:px-10 py-6">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Month</span>
            <Input
              type="month"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              data-testid="month-filter"
              className="w-44 h-8 rounded-none bg-neutral-900 border-neutral-800 text-xs"
            />
            {filterMonth && (
              <button onClick={() => setFilterMonth("")} className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-white">all months</button>
            )}
          </div>
          <div className="ml-auto inline-flex border border-neutral-800">
            <button
              onClick={() => setView("kanban")}
              data-testid="view-kanban"
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2 ${
                view === "kanban" ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-white"
              }`}
            >
              <LayoutGrid className="w-3 h-3" /> Kanban
            </button>
            <button
              onClick={() => setView("table")}
              data-testid="view-table"
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2 border-l border-neutral-800 ${
                view === "table" ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-white"
              }`}
            >
              <ListIcon className="w-3 h-3" /> Table
            </button>
          </div>
        </div>

        {/* Week tabs */}
        {filterMonth && weeks.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mr-1">Week</span>
            <button
              onClick={() => setFilterWeek("all")}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] border ${
                filterWeek === "all"
                  ? "border-white text-white"
                  : "border-neutral-800 text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid="week-all"
            >
              All
            </button>
            {weeks.map((w) => {
              const count = monthFiltered.filter((t) => isInWeek(t.due_date, w)).length;
              const active = filterWeek === w.key;
              return (
                <button
                  key={w.key}
                  onClick={() => setFilterWeek(w.key)}
                  data-testid={`week-${w.label.toLowerCase().replace(' ', '-')}`}
                  className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                    active ? "border-[#00C97A] text-[#00C97A]" : "border-neutral-800 text-neutral-500 hover:text-neutral-300"
                  }`}
                  title={w.range}
                >
                  {w.label} · {w.range} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* Filter */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button
            onClick={() => setFilterStatus("all")}
            className={`px-3 py-1 text-[10px] uppercase tracking-[0.2em] border ${
              filterStatus === "all"
                ? "border-white text-white"
                : "border-neutral-800 text-neutral-500 hover:text-neutral-300"
            }`}
            data-testid="filter-all"
          >
            All ({weekFiltered.length})
          </button>
          {STATUS_OPTIONS.map((s) => {
            const count = weekFiltered.filter((t) => t.status === s.value).length;
            const active = filterStatus === s.value;
            return (
              <button
                key={s.value}
                onClick={() => setFilterStatus(s.value)}
                data-testid={`filter-${s.value}`}
                className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] border transition-colors"
                style={
                  active
                    ? { borderColor: s.color, color: s.color }
                    : { borderColor: "#262626", color: "#737373" }
                }
              >
                {s.label} ({count})
              </button>
            );
          })}
        </div>

        {view === "kanban" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="kanban-board">
            {STATUS_OPTIONS.map((s) => {
              const colTasks = weekFiltered.filter((t) => t.status === s.value);
              const isOver = dragOver === s.value;
              return (
                <div
                  key={s.value}
                  className={`border flex flex-col transition-colors ${isOver ? "border-[#00C97A] bg-[#00C97A]/5" : "border-neutral-900"}`}
                  data-testid={`column-${s.value}`}
                  onDragOver={(e) => onDragOverCol(e, s.value)}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => onDropCol(e, s.value)}
                >
                  <div className="px-4 py-3 border-b border-neutral-900 flex items-center justify-between" style={{ borderTop: `2px solid ${s.color}` }}>
                    <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: s.color }}>{s.label}</div>
                    <div className="text-xs font-mono text-neutral-500">{colTasks.length}</div>
                  </div>
                  <div className="p-2 space-y-2 min-h-[120px]">
                    {colTasks.length === 0 && (
                      <div className="text-[10px] text-neutral-700 text-center py-6 uppercase tracking-[0.2em]">{isOver ? "Drop here" : "Empty"}</div>
                    )}
                    {colTasks.map((t) => {
                      const editor = findUser(t.assigned_editor_id);
                      const smm = findUser(t.assigned_smm_id);
                      const cli = findClient(t.client_id);
                      const ttype = TASK_TYPES.find((x) => x.value === t.task_type);
                      return (
                        <div
                          key={t.task_id}
                          draggable={canEdit}
                          onDragStart={(e) => onDragStart(e, t)}
                          className="border border-neutral-900 bg-neutral-950 p-3 hover:bg-neutral-900/70 transition-colors group cursor-grab active:cursor-grabbing"
                          data-testid={`kanban-card-${t.task_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-sm font-medium leading-tight flex-1">{t.title}</div>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setDetailTask(t); }}
                                className="inline-flex items-center gap-1 text-[10px] text-neutral-500 hover:text-[#00C97A] transition-colors"
                                title="Open chat"
                                data-testid={`chat-icon-${t.task_id}`}
                              >
                                💬
                              </button>
                              {canDelete && (
                                <button onClick={(e) => { e.stopPropagation(); handleDelete(t); }} className="text-neutral-700 group-hover:text-neutral-500 hover:text-red-400 transition-colors">
                                  <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {ttype && (
                              <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 bg-neutral-900 border border-neutral-800 text-neutral-400">
                                {ttype.label}
                              </span>
                            )}
                            {cli && (
                              <span className="text-[9px] uppercase tracking-[0.15em] text-[#00C97A]">{cli.name}</span>
                            )}
                            {t.reminder_at && (
                              <span className="text-[9px] uppercase tracking-[0.15em] text-amber-400" title={`Reminder: ${new Date(t.reminder_at).toLocaleString()}`}>
                                ⏰
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-neutral-500 mt-3 font-mono">
                            <span>{t.due_date || "—"}</span>
                            <span className="truncate">{editor?.name || smm?.name || "—"}</span>
                          </div>
                          {canEdit && (
                            <div className="mt-3 pt-2 border-t border-neutral-900">
                              <StatusToggle value={t.status} onChange={(v) => handleStatusChange(t, v)} />
                            </div>
                          )}
                          <div className="flex gap-2 mt-2">
                            {t.ref_video_link && (
                              <a href={t.ref_video_link} target="_blank" rel="noreferrer" className="text-[10px] text-neutral-400 hover:text-[#00C97A] inline-flex items-center gap-1">
                                <ExternalLink className="w-2.5 h-2.5" /> Ref
                              </a>
                            )}
                            {t.published_url && (
                              <a href={t.published_url} target="_blank" rel="noreferrer" className="text-[10px] text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1">
                                <ExternalLink className="w-2.5 h-2.5" /> Post
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <div className="border border-neutral-900 overflow-x-auto">
          <table className="w-full" data-testid="tasks-table">
            <thead>
              <tr className="border-b border-neutral-900 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                <th className="text-left px-4 py-3 font-medium">Task</th>
                <th className="text-left px-4 py-3 font-medium">Client</th>
                <th className="text-left px-4 py-3 font-medium">Editor</th>
                <th className="text-left px-4 py-3 font-medium">SMM</th>
                <th className="text-left px-4 py-3 font-medium">Due</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Refs</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-sm text-neutral-500">
                    No tasks yet. Create one to start scheduling.
                  </td>
                </tr>
              )}
              {filtered.map((t) => {
                const editor = findUser(t.assigned_editor_id);
                const smm = findUser(t.assigned_smm_id);
                const cli = findClient(t.client_id);
                return (
                  <tr
                    key={t.task_id}
                    onClick={() => setDetailTask(t)}
                    className="border-b border-neutral-900 hover:bg-neutral-900/40 cursor-pointer"
                    data-testid={`task-row-${t.task_id}`}
                  >
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-white">{t.title}</div>
                      {t.notes && (
                        <div className="text-xs text-neutral-500 mt-1 line-clamp-1">
                          {t.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-neutral-300">
                      {cli?.name || "—"}
                    </td>
                    <td className="px-4 py-4 text-sm text-neutral-300">
                      {editor?.name || "—"}
                    </td>
                    <td className="px-4 py-4 text-sm text-neutral-300">
                      {smm?.name || "—"}
                    </td>
                    <td className="px-4 py-4 text-xs font-mono text-neutral-400">
                      {t.due_date || "—"}
                    </td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <StatusToggle
                        value={t.status}
                        onChange={(v) => handleStatusChange(t, v)}
                        disabled={!canEdit}
                      />
                    </td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        {t.ref_video_link && (
                          <a
                            href={t.ref_video_link}
                            target="_blank"
                            rel="noreferrer"
                            className="text-neutral-400 hover:text-[#00C97A] inline-flex items-center gap-1 text-xs"
                            data-testid={`ref-link-${t.task_id}`}
                          >
                            <ExternalLink className="w-3 h-3" /> Ref
                          </a>
                        )}
                        {t.published_url && (
                          <a
                            href={t.published_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 text-xs"
                          >
                            <ExternalLink className="w-3 h-3" /> Post
                          </a>
                        )}
                      </div>
                    </td>
                  <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(t)}
                          className="text-neutral-500 hover:text-red-400 transition-colors"
                          data-testid={`delete-task-${t.task_id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
      <TaskDetailModal
        task={detailTask}
        open={!!detailTask}
        onClose={() => setDetailTask(null)}
        onUpdated={(updated) => {
          setTasks((p) => p.map((x) => x.task_id === updated.task_id ? updated : x));
          setDetailTask(updated);
        }}
        onDeleted={(t) => setTasks((p) => p.filter((x) => x.task_id !== t.task_id))}
        users={users}
        clients={clients}
      />
    </div>
  );
}
